import type { Face } from '@/lib/types/profile';

/**
 * Build a right-sized animated webp preview URL from a Mux playback id.
 *
 * The backend persists `preview.animatedUrl` as a bare
 * `image.mux.com/<id>/animated.webp` — full resolution, full frame rate.
 * Rendering that straight into a small avatar or card downloads far more than
 * the box needs (slow first paint), so derive a sized variant from the playback
 * id instead. Works for every tutor regardless of what's stored, since the
 * playback id is always present on the preview.
 */
export function muxAnimatedThumbnail(
  playbackId: string,
  { width, fps }: { width: number; fps: number }
): string {
  return `https://image.mux.com/${playbackId}/animated.webp?width=${width}&fps=${fps}`;
}

/**
 * A tiny still frame from a Mux playback id, to blur and stretch behind an
 * animated preview while that preview streams. At a few kilobytes it lands in
 * the first paint instead of racing the animation — which is what the stored
 * full-resolution `preview.thumbnailUrl` did, and lost, leaving the animation
 * to decode top-down over an empty card.
 */
export function muxBlurPoster(playbackId: string, { width }: { width: number }): string {
  return `https://image.mux.com/${playbackId}/thumbnail.webp?width=${width}`;
}

/**
 * The static-frame counterpart of an animated webp preview URL, kept at the same
 * width. Painted behind the animation so the box is never empty while the webp
 * streams or at each loop seam (the `fps` query param is inert on a still). Falls
 * back to the original URL if it isn't a Mux animated webp (e.g. a custom
 * preview override), in which case the layers are identical and harmless.
 */
export function muxStaticPoster(animatedUrl: string): string {
  return animatedUrl.replace('/animated.webp', '/thumbnail.webp');
}

/** The round that stands for a creator: their portrait, else a clip of their video.
 *  Width only: given both sides, Mux stretches the clip, so the circle crops it. */
export function faceUrl({ picture, playbackId }: Face, px: number): string | null {
  return picture ?? (playbackId ? muxAnimatedThumbnail(playbackId, { width: px, fps: 8 }) : null);
}
